// "My Approvals" inbox — the SHARED end-user approvals surface.
//
// End users see records pending THEIR approval, open the staged record in
// the REAL transaction form (the shared FormRenderer), and Approve / Reject /
// Edit it. A second tab lists the user's own submissions (any status) so they
// can track / withdraw them.
//
// The staged record is rendered read-only ("review mode"); an Edit button
// flips the form editable and "Save changes" persists the staged header via
// approvals.edit (NOT the live master). Every API call is wrapped in
// try/catch and header_json is guarded for null.
//
// Transport: the component carries NO HTTP client or toast library — all I/O
// and notifications ride the injected ``runtime`` (ApprovalsInboxRuntime, a
// TxnScreenRuntime with the approvals family + getModel required). Each host
// (the platform designer / Run view, the standalone runtime client) binds it
// to its own endpoints.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { tw } from '../../testHooks'
import {
  AlarmClock, AlertTriangle, BarChart3, CheckCircle2, ChevronDown, ChevronRight,
  ClipboardList, FileText, Inbox, Pencil,
  RefreshCcw, Save, Trash2, UserCog, Undo2, Users, X, XCircle, Zap,
} from 'lucide-react'
import type { ColumnDef, SqlModel, TransactionModel } from '../types'
import { normalizeItemChange } from '../types'
import { useHeaderItemChange } from '../screen/useHeaderItemChange'
import { attemptWithWarningConfirm } from '../warnings'
import { useWarningConfirm } from '../WarningsConfirmModal'
import type {
  ApprovalInstance, ApprovalChange, ApprovalChildChange,
  ApprovalSla, ApprovalAnalytics, ApprovalDelegation,
  EmbedDetailMap, ValidationErrorItem,
} from '../contracts'
import {
  buildParentPk, childFormsOf, columnFormKey, headerFormNo, mainSqlModel,
  parseApiError, visibleColumns,
} from '../utils'
import { TxnRuntimeProvider, type TxnNotifier } from '../context'
import FormRenderer from '../FormRenderer'
import FormTabs from '../list/FormTabs'
import ChildFormTab, { withLocalId, type ChildStore } from '../screen/ChildFormTab'
import { TxnScreenRuntimeProvider } from '../screen/screenContext'
import { DmsApprovalReview } from '../../dms/DmsApprovalReview'
import { DocumentDetail } from '../../dms/DocumentDetail'
import type {
  DmsApprovalDetail, DmsClient, DmsPendingApproval,
} from '../../dms/contracts'
import type { ApprovalsInboxRuntime } from './contract'
import {
  dmsKindOf, dmsRepoOf, dmsRowSubtitle, isDmsInstance, pendingSignoffOf,
  type DmsApprovalKind,
} from './dmsBridge'
import Spinner from './Spinner'

interface Props {
  /** Host-bound transport: the approvals family + list/getModel + txn. */
  runtime: ApprovalsInboxRuntime
  project: string
  onClose: () => void
  /** Lets the host re-read the pending count after a decision. */
  onCountChange?: (count: number) => void
  /**
   * Open the pending tab on EVERYONE's queue rather than the caller's own.
   * Only honoured for a super-approver (the server decides that, and the
   * toggle only appears once the list response says so) — a host sets it when
   * the user reached the inbox from an administrator's project-wide view, so
   * the row they clicked is actually in the list they land on.
   */
  initialShowAll?: boolean
  /**
   * Open this instance's review panel as soon as the list has loaded. Set by
   * a host that sent the user here from a row they had already picked (the
   * worklist), so they land on the record instead of hunting for it again.
   * Ignored once consumed, and silently if the instance is not in the list.
   */
  initialInstanceId?: string
  /**
   * Render INSIDE the host's surface instead of as a modal over it.
   *
   * The worklist shows this panel as one of its tabs, and a modal on top of a
   * panel reads as a second place rather than the same one: the user came
   * from a row two inches away, and stacking a dialog over it hides where
   * they were. Embedded, the inbox fills its container, drops the backdrop
   * and the portal, and leaves the surrounding chrome (title, tabs, close) to
   * the host that owns it.
   */
  embedded?: boolean
}

type Tab = 'pending' | 'submissions'

// A loaded transaction model keyed by obj_name, so opening several items for
// the same object doesn't refetch the metadata each time.
type ModelCache = Record<string, TransactionModel>

// ── Action-button approvals ───────────────────────────────────────────────
// An instance whose `action` is `action:<button>` stages a BUTTON CLICK, not a
// record draft: approving it RUNS the action. The record shown is the row the
// button was clicked on (context for the decision) — there is nothing to edit,
// so review mode is the only mode.
const ACTION_KIND_PREFIX = 'action:'

function isActionRequest(inst: ApprovalInstance | null | undefined): boolean {
  return String(inst?.action || '').toLowerCase().startsWith(ACTION_KIND_PREFIX)
}

function actionRequestLabel(inst: ApprovalInstance): string {
  const stamped = String(inst.action_label || '').trim()
  if (stamped) return stamped
  return String(inst.action || '').slice(ACTION_KIND_PREFIX.length).trim() || 'Action'
}

// Build the editable child-row buffer from a staged instance's details_json.
// Deep-copies every row, stamping a stable _localId on never-saved rows (React
// keys + reconciliation), and recurses into each row's grandchildren so the
// whole nested tree is editable. Tolerant of a null / malformed details_json.
function seedDetailsBuffer(
  details: Record<string, Array<Record<string, unknown>>> | null | undefined,
): EmbedDetailMap {
  const out: EmbedDetailMap = {}
  if (!details || typeof details !== 'object') return out
  for (const [formNo, rows] of Object.entries(details)) {
    if (!Array.isArray(rows)) continue
    out[String(formNo)] = rows.map((raw) => {
      const row: Record<string, unknown> = { ...(raw || {}) }
      const nested = row.__children
      if (nested && typeof nested === 'object') {
        row.__children = seedDetailsBuffer(
          nested as Record<string, Array<Record<string, unknown>>>,
        )
      }
      return row._txn_id ? row : withLocalId(row)
    })
  }
  return out
}

// Shape of the blocking-validation banner shown in the detail panel when an
// approve or a staged-record save is rejected by the server-side validations.
interface ValidationBanner {
  lead: string
  items: string[]
}

// Route a failed approve / staged-save to the detail panel: field-specific
// messages map onto the form (keyed by form_key, same as the live editor);
// everything is also listed in a blocking banner so the reviewer sees the
// full picture even while the form is read-only. Non-validation failures
// fall back to the plain error toast.
function routeValidationFailure(
  err: unknown, context: 'approve' | 'save',
  notify: TxnNotifier,
  setErrors: (v: Record<string, string>) => void,
  setBanner: (v: ValidationBanner | null) => void,
): void {
  const parsed = parseApiError(err)
  const items: ValidationErrorItem[] = parsed.validationErrors
  if (!items.length) {
    notify.error(`${context === 'approve' ? 'Approve' : 'Save'} failed: ${parsed.message}`)
    return
  }
  const fieldMap: Record<string, string> = {}
  const listed: string[] = []
  for (const it of items) {
    if (it.field && it.field !== '_record') fieldMap[it.field] = it.message
    listed.push(it.message)
  }
  setErrors(fieldMap)
  setBanner({
    lead: context === 'approve'
      ? 'This record does not pass validation, so it cannot be approved. Use Edit to fix the staged data, or reject it.'
      : 'The changes do not pass validation — nothing was saved.',
    items: listed,
  })
  notify.error(context === 'approve'
    ? 'Validation failed — the record cannot be approved as-is'
    : 'Validation failed — changes not saved')
}

export default function ApprovalsInbox({
  runtime, project, onClose, onCountChange, initialShowAll = false,
  initialInstanceId = '', embedded = false,
}: Props) {
  const notify = runtime.txn.notify
  const [tab, setTab] = useState<Tab>('pending')
  const [items, setItems] = useState<ApprovalInstance[]>([])
  const [loading, setLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  // Super-approver (admin / FUNC-ADMIN) surfacing. `isSuper` is learned from the
  // pending-list response; only then do we show the "show everyone's pending"
  // toggle. `showAll` (default OFF) flips the pending fetch to the whole project.
  const [isSuper, setIsSuper] = useState(false)
  const [showAll, setShowAll] = useState(initialShowAll)

  // The item open in the detail panel (a fresh approvalGet result so we always
  // show the latest staged header + history), plus its loaded transaction model.
  const [selected, setSelected] = useState<ApprovalInstance | null>(null)
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  // ── Document (DMS) approvals ──────────────────────────────────────────
  // The open instance's document detail, when it stages a DOCUMENT rather
  // than a transaction record. Non-null and `model` stays null: the two are
  // alternatives, not layers (see ./dmsBridge).
  const [dmsDetail, setDmsDetail] = useState<DmsApprovalDetail | null>(null)
  // Document titles for the LIST, keyed by instance id. The staged header is
  // a pointer, so a row's own data cannot name the document — one
  // `pendingApprovals()` per repository resolves every row it owns at once,
  // which is cheaper than one detail call per row and is the same list the
  // repository's own queue reads.
  const [dmsRows, setDmsRows] = useState<Record<string, DmsPendingApproval>>({})
  const [detailBusy, setDetailBusy] = useState(false)
  // Field-level diff (old → new) for an EDIT submission, from approvalGet.
  const [changes, setChanges] = useState<ApprovalChange[]>([])
  // Per-child-form row changes (added / removed / modified) for an EDIT of a
  // header→detail transaction, also from approvalGet.
  const [childChanges, setChildChanges] = useState<ApprovalChildChange[]>([])

  // Review vs. edit. In review the form is read-only; Edit flips it editable
  // and seeds an editable copy of the staged header.
  const [editing, setEditing] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  // Staged child rows keyed by child form_no (each row carries its own
  // __children), seeded from the instance's details_json. The reviewer sees
  // these in the child tabs and — in edit mode — can change them; the buffer
  // is sent back verbatim on save and replayed onto the master at promotion.
  const [detailsBuffer, setDetailsBuffer] = useState<EmbedDetailMap>({})
  const detailsBufferRef = useRef<EmbedDetailMap>({})
  useEffect(() => { detailsBufferRef.current = detailsBuffer }, [detailsBuffer])
  // Child forms currently lazy-loading their master rows (de-dupe concurrent opens).
  const loadingFormsRef = useRef<Set<string>>(new Set())
  // Which form tab is open (header form_no, or one of its child form_nos).
  const [activeFormNo, setActiveFormNo] = useState<string>('')

  // Reject requires a comment; this drives the inline reject box.
  const [rejecting, setRejecting] = useState(false)
  const [comment, setComment] = useState('')

  // Blocking validation feedback for approve / staged-record save: per-field
  // messages rendered on the form + a banner listing every failure. Warnings
  // (severity 'W') instead go through the acknowledge-and-retry modal.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [validationBanner, setValidationBanner] = useState<ValidationBanner | null>(null)
  const { confirmWarnings, warningsModal } = useWarningConfirm()

  // ── Phase 3 ───────────────────────────────────────────────────────────────
  // Bulk select (pending tab only): the set of selected instance ids + a busy
  // flag for the bulk action bar.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)

  // SLA position of the currently-open instance (from approvalGet.sla).
  const [sla, setSla] = useState<ApprovalSla | null>(null)

  // Out-of-office delegation. `delegatingFor` is the list of user ids whose
  // approval authority the caller is currently borrowing (from approvalList).
  const [delegatingFor, setDelegatingFor] = useState<string[]>([])
  const [showDelegate, setShowDelegate] = useState(false)
  const [delegations, setDelegations] = useState<ApprovalDelegation[]>([])
  const [delegLoading, setDelegLoading] = useState(false)

  // Analytics panel (super-approver only).
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [analytics, setAnalytics] = useState<ApprovalAnalytics | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)

  const modelCache = useRef<ModelCache>({})
  // `initialInstanceId` is honoured ONCE: after that the user's own
  // navigation owns the detail panel, and re-opening the row they just closed
  // would be a trap.
  const initialOpened = useRef(false)

  // The DMS transport for whichever repository an instance belongs to. Null
  // when the instance is not a document approval, or when the host binds no
  // document module at all — in which case the panel says so rather than
  // offering decisions it cannot carry out.
  const dmsClientFor = useCallback((
    inst: ApprovalInstance | null | undefined,
  ): DmsClient | null => {
    // A submitted-but-not-yet-raised row stages nothing, so `dmsRepoOf` — which
    // reads the staged pointer — cannot name its repository; the row carries
    // the name itself.
    const repo = dmsRepoOf(inst) || pendingSignoffOf(inst)?.repo || ''
    if (!repo || !runtime.dms) return null
    try {
      return runtime.dms(repo)
    } catch {
      return null
    }
  }, [runtime])

  // ── List loading ────────────────────────────────────────────────────────
  const load = useCallback(async (which: Tab) => {
    setLoading(true)
    setListError(null)
    try {
      const res = which === 'pending'
        ? await runtime.approvals.list(showAll ? { all: true } : {})
        : await runtime.approvals.submissions()
      const list = Array.isArray(res?.items) ? res.items : []
      setItems(list)
      // The list changed underneath us — drop any stale selection.
      setSelectedIds(new Set())
      if (which === 'pending') {
        // Learn whether the caller is a super-approver so the header can offer
        // the "show all pending" toggle. (Only present on the pending response.)
        setIsSuper(!!(res as { is_super_approver?: boolean })?.is_super_approver)
        // Phase 3: whose approval authority the caller is currently borrowing.
        const dele = (res as { delegating_for?: string[] })?.delegating_for
        setDelegatingFor(Array.isArray(dele) ? dele : [])
        onCountChange?.(typeof res?.count === 'number' ? res.count : list.length)
      } else {
        setDelegatingFor([])
      }
    } catch (err) {
      const { message } = parseApiError(err)
      setListError(message)
      setItems([])
      notify.error(`Could not load approvals: ${message}`)
    } finally {
      setLoading(false)
    }
  }, [runtime, notify, onCountChange, showAll])

  // Re-fetch on tab change and whenever the super-approver "show all" toggle
  // flips (load's identity already changes with showAll, so this covers both).
  useEffect(() => { void load(tab) }, [tab, load])

  // Name the documents behind any DMS rows in the current list.
  //
  // Best-effort throughout: a repository the caller cannot list, or a host with
  // no document module, simply leaves those rows with their generic subtitle.
  // A row that cannot be named is still a row that can be opened.
  useEffect(() => {
    const repos = Array.from(new Set(
      items.filter(isDmsInstance).map((i) => dmsRepoOf(i)).filter(Boolean)))
    if (repos.length === 0 || !runtime.dms) {
      setDmsRows({})
      return undefined
    }
    let live = true
    void (async () => {
      const merged: Record<string, DmsPendingApproval> = {}
      await Promise.all(repos.map(async (repo) => {
        let client: DmsClient
        try {
          client = runtime.dms!(repo)
        } catch {
          return
        }
        if (!client.pendingApprovals) return
        try {
          for (const row of await client.pendingApprovals()) {
            if (row?.instance_id) merged[String(row.instance_id)] = row
          }
        } catch {
          // A repository the caller may not list is not an inbox failure.
        }
      }))
      if (live) setDmsRows(merged)
    })()
    return () => { live = false }
  }, [items, runtime])

  // ── Transaction-model loading ─────────────────────────────────────────────
  // runtime.getModel resolves the object's TransactionModel (each host
  // unwraps its own envelope); a null-ish result is a hard error here.
  const loadModelFor = useCallback(async (objName: string): Promise<TransactionModel> => {
    const cached = modelCache.current[objName]
    if (cached) return cached
    const loaded = await runtime.getModel(objName)
    if (!loaded) {
      throw new Error(`No transaction model found for "${objName}"`)
    }
    modelCache.current[objName] = loaded
    return loaded
  }, [runtime])

  // Open one item in the detail panel: refetch it (latest staged record +
  // history) and load its transaction model so FormRenderer can mount.
  const openItem = useCallback(async (inst: ApprovalInstance) => {
    setSelected(inst)
    setModel(null)
    setModelError(null)
    setEditing(false)
    setRejecting(false)
    setComment('')
    setChanges([])
    setChildChanges([])
    setDetailsBuffer({})
    loadingFormsRef.current.clear()
    setActiveFormNo('')
    setSla(null)
    setDmsDetail(null)
    setFieldErrors({})
    setValidationBanner(null)
    setDetailBusy(true)
    try {
      // A document waiting for a sign-off nobody has raised has no instance
      // behind it — `id` is a worklist task key. Fetching it answers "not
      // found", there is no staged record to diff and no chain to show; the
      // row opens the DOCUMENT (see the detail pane below).
      if (pendingSignoffOf(inst)) return
      // Refresh the instance so we render the freshest staged record + history.
      let fresh = inst
      try {
        const got = await runtime.approvals.get(inst.id, inst.obj_name || undefined)
        if (got?.instance) fresh = got.instance
        setChanges(Array.isArray(got?.changes) ? got.changes : [])
        setChildChanges(Array.isArray(got?.child_changes) ? got.child_changes : [])
        setSla(got?.sla ?? null)
      } catch {
        // Non-fatal: fall back to the list row we already have.
      }
      setSelected(fresh)
      setFormValues({ ...(fresh.header_json || {}) })
      setDetailsBuffer(seedDetailsBuffer(fresh.details_json))
      // A DOCUMENT approval has no transaction model and never had one —
      // loading it only ever produced "No transaction model found for
      // DMS_<repo>". Review it as the document it is instead.
      if (isDmsInstance(fresh)) {
        const client = dmsClientFor(fresh)
        if (client?.approvalDetail) {
          setDmsDetail(await client.approvalDetail(fresh.id))
        }
        return
      }
      const m = await loadModelFor(fresh.obj_name)
      setModel(m)
      setActiveFormNo(headerFormNo(m))
    } catch (err) {
      const { message } = parseApiError(err)
      setModelError(message)
      notify.error(`Could not open record: ${message}`)
    } finally {
      setDetailBusy(false)
    }
  }, [runtime, notify, loadModelFor, dmsClientFor])

  // Land the caller on the row they picked elsewhere, once the list holds it.
  useEffect(() => {
    if (!initialInstanceId || initialOpened.current || loading) return
    const inst = items.find((i) => i.id === initialInstanceId)
    if (!inst) return
    initialOpened.current = true
    void openItem(inst)
  }, [initialInstanceId, items, loading, openItem])

  const closeDetail = useCallback(() => {
    if (editing) {
      const ok = window.confirm('Discard your unsaved edits to this record?')
      if (!ok) return
    }
    setSelected(null)
    setModel(null)
    setModelError(null)
    setEditing(false)
    setRejecting(false)
    setComment('')
    setSla(null)
    setDmsDetail(null)
    setFieldErrors({})
    setValidationBanner(null)
  }, [editing])

  // After a decision: refresh the list, drop the detail panel, toast the
  // server message. If the just-decided item was open, close it.
  const afterDecision = useCallback(async (message: string) => {
    notify.success(message || 'Done')
    setSelected(null)
    setModel(null)
    setEditing(false)
    setRejecting(false)
    setComment('')
    setSla(null)
    setDmsDetail(null)
    await load(tab)
  }, [notify, load, tab])

  // ── Decisions ─────────────────────────────────────────────────────────
  // A DOCUMENT decision goes to the repository, never to the transaction
  // approval path: approving one moves `current_version_id` and advances the
  // document's state, which the executor (looking for a transaction model
  // called `DMS_<repo>`) cannot do and now refuses to attempt.
  //
  // The wording distinguishes a CLEARED LEVEL from a DECIDED CHAIN, because
  // the difference matters to the person who just clicked: a multi-level
  // document is not published because you approved it.
  const decideDms = useCallback(async (
    decision: 'approve' | 'reject', comment = '',
  ) => {
    if (!selected) return
    const client = dmsClientFor(selected)
    if (!client?.decideApproval) {
      notify.error(
        'This document approval is decided in its repository — this app '
        + 'is not serving the document module here.')
      return
    }
    const title = dmsDetail?.title || dmsRows[selected.id]?.title || 'The document'
    const res = await client.decideApproval({
      instanceId: selected.id, decision, comment,
    })
    const status = String(res?.status || '')
    await afterDecision(
      status === 'approved'
        ? `Approved. "${title}" is now the current release.`
        : status === 'rejected'
          ? `Rejected. "${title}" has gone back to its owner with your reason.`
          : `Recorded. Your level is cleared; "${title}" is now with the next approver.`)
  }, [selected, dmsClientFor, dmsDetail, dmsRows, notify, afterDecision])

  // Approve runs the SAME validations a save would against the record that
  // would be written (the data may have drifted while pending). Warnings ask
  // for acknowledgement and retry; blocking errors keep the instance pending
  // and light up the form + banner so the approver can Edit or reject.
  const doApprove = useCallback(async () => {
    if (!selected) return
    setDetailBusy(true)
    setFieldErrors({})
    setValidationBanner(null)
    try {
      // A document approval promotes by moving the repository's version
      // pointer, so it is decided by the repository — the transaction path
      // refuses it outright rather than half-doing it.
      if (isDmsInstance(selected)) {
        await decideDms('approve')
        return
      }
      const res = await attemptWithWarningConfirm(
        (ignore) => runtime.approvals.approve(selected.id, {
          objName: selected.obj_name || undefined,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
          ...(ignore.length ? { ignoreWarnings: ignore } : {}),
        }),
        confirmWarnings,
      )
      await afterDecision(res?.message || 'Approved')
    } catch (err) {
      routeValidationFailure(err, 'approve', notify, setFieldErrors, setValidationBanner)
    } finally {
      setDetailBusy(false)
    }
  }, [runtime, notify, selected, comment, afterDecision, confirmWarnings, decideDms])

  const doReject = useCallback(async () => {
    if (!selected) return
    const text = comment.trim()
    if (!text) {
      notify.error('A comment is required to reject.')
      return
    }
    setDetailBusy(true)
    try {
      if (isDmsInstance(selected)) {
        await decideDms('reject', text)
        return
      }
      const res = await runtime.approvals.reject(selected.id, {
        objName: selected.obj_name || undefined,
        comment: text,
      })
      await afterDecision(res?.message || 'Rejected')
    } catch (err) {
      notify.error(`Reject failed: ${parseApiError(err).message}`)
    } finally {
      setDetailBusy(false)
    }
  }, [runtime, notify, selected, comment, afterDecision, decideDms])

  const startEdit = useCallback(() => {
    if (!selected) return
    setFormValues({ ...(selected.header_json || {}) })
    setDetailsBuffer(seedDetailsBuffer(selected.details_json))
    setEditing(true)
    setRejecting(false)
    // Keep any approve-failure field errors visible — they mark exactly what
    // the approver opened the editor to fix.
  }, [selected])

  const cancelEdit = useCallback(() => {
    if (selected) {
      setFormValues({ ...(selected.header_json || {}) })
      setDetailsBuffer(seedDetailsBuffer(selected.details_json))
    }
    setEditing(false)
    setFieldErrors({})
    setValidationBanner(null)
  }, [selected])

  const saveEdit = useCallback(async () => {
    if (!selected) return
    setDetailBusy(true)
    setFieldErrors({})
    setValidationBanner(null)
    try {
      // Only send details for a header→detail transaction that actually staged
      // child rows — a header-only edit leaves details_json untouched. The
      // server validates the edited payload like a real save; warnings prompt
      // for acknowledgement, blocking errors land on the form + banner.
      const hasDetails = Object.keys(detailsBuffer).length > 0
      const res = await attemptWithWarningConfirm(
        (ignore) => runtime.approvals.edit(selected.id, formValues, {
          objName: selected.obj_name || undefined,
          ...(hasDetails ? { details: detailsBuffer } : {}),
          ...(ignore.length ? { ignoreWarnings: ignore } : {}),
        }),
        confirmWarnings,
      )
      notify.success(res?.message || 'Changes saved')
      // Re-read the staged record so the review view shows the saved values +
      // the new "edited" history event; stay on this item, back in review mode.
      setEditing(false)
      try {
        const got = await runtime.approvals.get(selected.id, selected.obj_name || undefined)
        if (got?.instance) {
          setSelected(got.instance)
          setFormValues({ ...(got.instance.header_json || {}) })
          setDetailsBuffer(seedDetailsBuffer(got.instance.details_json))
          setChanges(Array.isArray(got?.changes) ? got.changes : [])
          setChildChanges(Array.isArray(got?.child_changes) ? got.child_changes : [])
        }
      } catch {
        // Non-fatal — the edit succeeded even if the re-read failed.
      }
      // Reflect any staged-value change in the underlying list too.
      await load(tab)
    } catch (err) {
      routeValidationFailure(err, 'save', notify, setFieldErrors, setValidationBanner)
    } finally {
      setDetailBusy(false)
    }
  }, [runtime, notify, selected, formValues, detailsBuffer, load, tab, confirmWarnings])

  const doWithdraw = useCallback(async () => {
    if (!selected) return
    if (!window.confirm('Withdraw this submission? It will no longer be pending.')) return
    setDetailBusy(true)
    try {
      const res = await runtime.approvals.withdraw(selected.id, selected.obj_name || undefined)
      await afterDecision(res?.message || 'Withdrawn')
    } catch (err) {
      notify.error(`Withdraw failed: ${parseApiError(err).message}`)
    } finally {
      setDetailBusy(false)
    }
  }, [runtime, notify, selected, afterDecision])

  // Edit mode runs the form's item_change exactly as the entry screen does
  // (the shared header hook): derived values, protect state, warnings. The
  // staged record is an ADD when the request creates one, else an EDIT.
  const icSm = useMemo(() => (model ? mainSqlModel(model) : null), [model])
  const [editProtect, setEditProtect] = useState<Record<string, 0 | 1>>({})
  const executeFunnel = useCallback(
    (o: string, a: string, p?: Record<string, unknown>) => runtime.execute(o, a, p),
    [runtime],
  )
  const selectedActionRef = useRef<string>('')
  useEffect(() => { selectedActionRef.current = selected?.action || '' }, [selected])
  const itemChangeMode = useCallback(
    (): 'add' | 'edit' => (selectedActionRef.current === 'add' ? 'add' : 'edit'), [])
  const triggerItemChange = useHeaderItemChange({
    execute: executeFunnel,
    notify,
    objName: selected?.obj_name || '',
    sm: icSm,
    setFormValues,
    setDynamicProtect: setEditProtect,
    editMode: itemChangeMode,
  })
  useEffect(() => { if (!editing) setEditProtect({}) }, [editing])

  const onFieldChange = useCallback((name: string, v: unknown) => {
    const next = { ...formValues, [name]: v }
    setFormValues(next)
    if (!editing || !icSm) return
    const col = icSm.columns?.find(w => {
      const c = w.column
      if (!c) return false
      if (columnFormKey(c as ColumnDef) === name || c.name === name) return true
      const dbn = (c.db_name || '').replace(/\s+/g, '_').toLowerCase()
      return !!dbn && dbn === name
    })?.column
    if (col && normalizeItemChange(col.item_change)) {
      triggerItemChange(columnFormKey(col as ColumnDef), next)
    }
  }, [formValues, editing, icSm, triggerItemChange])

  // ── Phase 3: bulk approve / reject (pending tab only) ───────────────────────
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Select-all over the pending instances (only pending rows are selectable).
  const pendingIds = useMemo(
    () => items.filter((i) => i.status === 'pending').map((i) => i.id),
    [items],
  )
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedIds.has(id))
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const everySelected = pendingIds.length > 0 && pendingIds.every((id) => prev.has(id))
      return everySelected ? new Set<string>() : new Set<string>(pendingIds)
    })
  }, [pendingIds])

  const doBulk = useCallback(async (decision: 'approve' | 'reject') => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    let comment: string | undefined
    if (decision === 'reject') {
      // A comment is optional for a bulk reject — window.prompt is acceptable here.
      const entered = window.prompt(
        `Reject ${ids.length} selected approval(s)? Optionally add a comment:`,
        '',
      )
      if (entered === null) return // user cancelled
      const trimmed = entered.trim()
      if (trimmed) comment = trimmed
    }
    setBulkBusy(true)
    try {
      // Document approvals cannot ride the engine's bulk endpoint — that
      // promotes by writing transaction rows. They are decided one at a time
      // through their repository and folded into the same tally, so a mixed
      // selection behaves as one action to the person who made it.
      const byId = new Map(items.map((i) => [i.id, i]))
      const docIds = ids.filter((id) => isDmsInstance(byId.get(id)))
      const txnIds = ids.filter((id) => !isDmsInstance(byId.get(id)))
      let succeeded = 0
      let failed = 0
      if (txnIds.length > 0) {
        const res = await runtime.approvals.bulk(txnIds, {
          decision,
          ...(comment ? { comment } : {}),
        })
        succeeded += res.succeeded
        failed += res.failed
      }
      for (const id of docIds) {
        const inst = byId.get(id)
        const client = dmsClientFor(inst)
        try {
          if (!client?.decideApproval) throw new Error('no document transport')
          await client.decideApproval({
            instanceId: id, decision, comment: comment || '',
          })
          succeeded += 1
        } catch {
          failed += 1
        }
      }
      const verb = decision === 'approve' ? 'approved' : 'rejected'
      const summary = `${succeeded} ${verb}${failed ? `, ${failed} failed` : ''}`
      if (failed > 0) (notify.warn ?? notify.info ?? notify.success)(summary)
      else notify.success(summary)
      setSelectedIds(new Set())
      // If the open record was part of the batch, drop the detail panel.
      if (selected && ids.includes(selected.id)) {
        setSelected(null)
        setModel(null)
        setSla(null)
        setDmsDetail(null)
      }
      await load(tab)
    } catch (err) {
      notify.error(`Bulk ${decision} failed: ${parseApiError(err).message}`)
    } finally {
      setBulkBusy(false)
    }
  }, [runtime, notify, selectedIds, selected, items, dmsClientFor, load, tab])

  // ── Phase 3: out-of-office delegation ───────────────────────────────────────
  const loadDelegations = useCallback(async () => {
    setDelegLoading(true)
    try {
      const res = await runtime.approvals.delegates.list({})
      setDelegations(Array.isArray(res?.items) ? res.items : [])
    } catch (err) {
      notify.error(`Could not load delegations: ${parseApiError(err).message}`)
      setDelegations([])
    } finally {
      setDelegLoading(false)
    }
  }, [runtime, notify])

  const openDelegate = useCallback(() => {
    setShowDelegate(true)
    void loadDelegations()
  }, [loadDelegations])

  const submitDelegation = useCallback(async (
    toUser: string,
    opts: { startsAt?: string; endsAt?: string; reason?: string },
  ) => {
    const target = toUser.trim()
    if (!target) {
      notify.error('Enter the user to delegate to.')
      return
    }
    try {
      await runtime.approvals.delegates.set(target, {
        ...(opts.startsAt ? { startsAt: opts.startsAt } : {}),
        ...(opts.endsAt ? { endsAt: opts.endsAt } : {}),
        ...(opts.reason ? { reason: opts.reason } : {}),
      })
      notify.success(`Approvals delegated to ${target}`)
      await loadDelegations()
    } catch (err) {
      notify.error(`Delegate failed: ${parseApiError(err).message}`)
    }
  }, [runtime, notify, loadDelegations])

  const revokeDelegation = useCallback(async (id: number) => {
    try {
      await runtime.approvals.delegates.revoke(id)
      notify.success('Delegation revoked')
      await loadDelegations()
    } catch (err) {
      notify.error(`Revoke failed: ${parseApiError(err).message}`)
    }
  }, [runtime, notify, loadDelegations])

  // ── Phase 3: analytics (super-approver only) ────────────────────────────────
  const openAnalytics = useCallback(async () => {
    setShowAnalytics(true)
    setAnalyticsLoading(true)
    try {
      const res = await runtime.approvals.analytics()
      setAnalytics(res?.analytics ?? null)
    } catch (err) {
      notify.error(`Could not load analytics: ${parseApiError(err).message}`)
      setAnalytics(null)
    } finally {
      setAnalyticsLoading(false)
    }
  }, [runtime, notify])

  // What the open instance IS decides the whole right-hand pane: a document
  // approval renders the document, a transaction one renders its form.
  const dmsKind: DmsApprovalKind = useMemo(() => dmsKindOf(selected), [selected])
  // Non-null while the open row is a document SUBMITTED for a sign-off nobody
  // has raised yet — no instance, nothing staged, nothing to decide here.
  const preSignoff = useMemo(() => pendingSignoffOf(selected), [selected])
  const dmsClient = useMemo(
    () => ((dmsKind || preSignoff) ? dmsClientFor(selected) : null),
    [dmsKind, preSignoff, selected, dmsClientFor])
  const dmsTitle = useMemo(() => (
    dmsDetail?.title
    || (selected ? dmsRows[selected.id]?.title : '')
    || ''
  ), [dmsDetail, dmsRows, selected])

  const sm = useMemo(() => (model ? mainSqlModel(model) : null), [model])
  const headerFormNoStr = useMemo(() => (model ? headerFormNo(model) : ''), [model])
  // This transaction's direct child forms (header→detail). Empty for a flat
  // transaction, in which case no tab strip is shown.
  const childForms = useMemo(
    () => (model && headerFormNoStr ? childFormsOf(model, headerFormNoStr) : []),
    [model, headerFormNoStr],
  )
  const topForms = useMemo(
    () => (sm ? [{ formNo: headerFormNoStr, sm }, ...childForms] : []),
    [sm, headerFormNoStr, childForms],
  )
  // The header's key values — the parent_pk each direct child row is scoped by
  // (the staged record's header is the parent for every child here).
  const parentPk = useMemo(
    () => (sm ? buildParentPk(sm, formValues) : {}),
    [sm, formValues],
  )
  const parentPkKey = useMemo(() => JSON.stringify(parentPk), [parentPk])

  // A ChildStore over `detailsBuffer` for one direct child form. Unlike the
  // live-preview editor, `ensureLoaded` is a NO-OP: the staged rows already
  // live in the buffer (seeded from details_json), so we must never overwrite
  // them with the master's current rows.
  const makeApprovalStore = useCallback((childFormNo: string): ChildStore => ({
    rows: detailsBuffer[childFormNo] || [],
    add: (row) => setDetailsBuffer(b => ({
      ...b, [childFormNo]: [...(b[childFormNo] || []), withLocalId(row)],
    })),
    edit: (index, patch) => setDetailsBuffer(b => {
      const rows = (b[childFormNo] || []).slice()
      const target = rows[index]
      if (!target) return b
      const { _error, _fieldErrors, ...rest } = target as Record<string, unknown>
      void _error; void _fieldErrors
      rows[index] = { ...rest, ...patch }
      return { ...b, [childFormNo]: rows }
    }),
    remove: (index) => setDetailsBuffer(b => {
      const rows = b[childFormNo] || []
      const target = rows[index]
      if (!target) return b
      // A saved row is flagged deleted (so promotion removes it); a never-saved
      // row just drops out of the buffer.
      const nextRows = target._txn_id
        ? rows.map((r, i) => (i === index ? { ...r, _deleted: true } : r))
        : rows.filter((_, i) => i !== index)
      return { ...b, [childFormNo]: nextRows }
    }),
    // Forms the submitter touched are already in the buffer (staged) — never
    // reload those. For an EDIT, a child form the submitter left untouched is
    // absent from details_json; lazy-load its CURRENT master rows so the
    // reviewer sees the full picture (and can edit them too). Adds have no
    // master record, so nothing to load.
    ensureLoaded: async (childParentPk) => {
      if (!selected || selected.action !== 'edit' || !selected.target_txn_id) return
      if (!childParentPk || Object.keys(childParentPk).length === 0) return
      if (Object.prototype.hasOwnProperty.call(detailsBufferRef.current, childFormNo)) return
      if (loadingFormsRef.current.has(childFormNo)) return
      loadingFormsRef.current.add(childFormNo)
      try {
        const res = await runtime.list(selected.obj_name, {
          limit: 200, offset: 0, formNo: childFormNo, parentPk: childParentPk,
        })
        setDetailsBuffer(b => (
          Object.prototype.hasOwnProperty.call(b, childFormNo)
            ? b
            : { ...b, [childFormNo]: (res.rows as Array<Record<string, unknown>>) || [] }
        ))
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.debug('approval child lazy-load failed (best-effort):', err)
        }
      } finally {
        loadingFormsRef.current.delete(childFormNo)
      }
    },
  }), [detailsBuffer, runtime, selected])

  // ── Render ────────────────────────────────────────────────────────────
  const node = (
    <div
      onClick={embedded ? undefined : onClose}
      style={embedded ? {
        // Embedded: no backdrop, no dimming — the host's panel IS the surface.
        display: 'flex', flexDirection: 'column',
        height: '100%', minHeight: 0, width: '100%',
      } : {
        position: 'fixed', inset: 0, zIndex: 9430,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={embedded ? undefined : (e) => e.stopPropagation()}
        // Test hooks (testHooks.ts): the inbox is a dialog the Testing tool
        // drives to check a submission reached approval and to approve it.
        // `loading` / `scope` let it wait for the list (the super-approver
        // toggle only exists once the first response says so) instead of
        // reading an inbox that has not answered yet.
        data-tw-dialog="approvals"
        data-tw-loading={loading ? '1' : '0'}
        data-tw-scope={showAll ? 'all' : 'mine'}
        style={embedded ? {
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          width: '100%', height: '100%', minHeight: 0,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        } : {
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          width: 'min(1100px, 96vw)',
          height: 'min(800px, 92vh)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        {/* Header + tabs */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px', borderBottom: '1px solid var(--color-border)',
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <strong style={{ fontSize: '1.0125rem', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Inbox size={16} /> My Approvals
            </strong>
            <div style={{ display: 'inline-flex', gap: 2, background: 'var(--color-bg-secondary)', padding: 3, borderRadius: 8 }}>
              <TabPill
                active={tab === 'pending'}
                icon={<Inbox size={13} />}
                label="Pending my approval"
                onClick={() => { setTab('pending'); setSelected(null) }}
              />
              <TabPill
                active={tab === 'submissions'}
                icon={<ClipboardList size={13} />}
                label="My submissions"
                onClick={() => { setTab('submissions'); setSelected(null) }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {tab === 'pending' && isSuper && (
              <label
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: '0.8225rem', color: 'var(--color-text-secondary)',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
                title="As an admin (FUNC-ADMIN) you can review and approve every pending approval in this project."
              >
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => { setSelected(null); setShowAll(e.target.checked) }}
                  disabled={loading}
                  {...tw('action', 'approvals-show-all')}
                />
                Show all pending approvals (admin)
              </label>
            )}
            {isSuper && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => void openAnalytics()}
                title="Project-wide approval analytics"
              >
                <BarChart3 size={14} /> Analytics
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm"
              onClick={openDelegate}
              title="Delegate your approvals while you're away (out of office)"
            >
              <UserCog size={14} /> Out of office
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => void load(tab)} disabled={loading} title="Refresh">
              <RefreshCcw size={14} />
            </button>
            {!embedded && (
              <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close" {...tw('action', 'approvals-close')}>
                <X size={14} /> Close
              </button>
            )}
          </div>
        </div>

        {/* Phase 3: "you're covering for…" banner */}
        {tab === 'pending' && delegatingFor.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 18px', borderBottom: '1px solid var(--color-border)',
            background: 'rgba(99,102,241,0.10)', color: 'var(--color-text)',
            fontSize: '0.8425rem',
          }}>
            <Users size={14} style={{ flexShrink: 0, opacity: 0.8 }} />
            <span>
              You&rsquo;re covering approvals for:{' '}
              <strong>{delegatingFor.join(', ')}</strong>
            </span>
          </div>
        )}

        {/* Phase 3: bulk action bar (pending tab, ≥1 selected) */}
        {tab === 'pending' && selectedIds.size > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 18px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <span style={{ fontSize: '0.8625rem', fontWeight: 600 }}>
              {selectedIds.size} selected
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkBusy}
            >
              Clear
            </button>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => void doBulk('reject')}
              disabled={bulkBusy}
              style={{ color: 'var(--color-error, #ef4444)' }}
              title="Reject the selected approvals"
            >
              <XCircle size={14} /> Reject selected ({selectedIds.size})
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => void doBulk('approve')}
              disabled={bulkBusy}
              title="Approve the selected approvals"
            >
              <CheckCircle2 size={14} /> Approve selected ({selectedIds.size})
            </button>
          </div>
        )}

        {/* Body: master list + detail panel */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {/* Master list */}
          <div style={{
            width: selected ? 360 : '100%',
            flexShrink: 0,
            borderRight: selected ? '1px solid var(--color-border)' : 'none',
            overflow: 'auto',
            transition: 'width 0.15s ease',
          }}>
            <ApprovalList
              items={items}
              dmsRows={dmsRows}
              loading={loading}
              error={listError}
              tab={tab}
              selectedId={selected?.id ?? null}
              compact={!!selected}
              onOpen={openItem}
              selectable={tab === 'pending'}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              allSelected={allSelected}
              onToggleSelectAll={toggleSelectAll}
            />
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <DetailHeader
                inst={selected}
                tab={tab}
                editing={editing}
                busy={detailBusy}
                sla={sla}
                dmsKind={dmsKind}
                dmsTitle={dmsTitle}
                canDecideDms={!!dmsClient?.decideApproval}
                onClose={closeDetail}
                onApprove={doApprove}
                onStartReject={() => { setRejecting(true); setComment('') }}
                onEdit={startEdit}
                onCancelEdit={cancelEdit}
                onSave={saveEdit}
                onWithdraw={doWithdraw}
              />

              {rejecting && (
                <RejectBox
                  comment={comment}
                  busy={detailBusy}
                  onChange={setComment}
                  onCancel={() => { setRejecting(false); setComment('') }}
                  onConfirm={doReject}
                />
              )}

              <div style={{ flex: 1, overflow: 'auto', padding: '0 0 16px' }}>
                {preSignoff ? (
                  <div style={{ padding: '14px 18px 0' }}>
                    <DmsNotice text={
                      preSignoff.pending_roles && preSignoff.pending_roles.length
                        ? `This document is with ${preSignoff.pending_roles.join(', ')} `
                          + `for sign-off. Until one of them acts there is no `
                          + `approval to track, so it cannot be withdrawn from here.`
                        : 'This document has been submitted and is waiting for its '
                          + 'reviewers. Until one of them acts there is no approval '
                          + 'to track, so it cannot be withdrawn from here.'} />
                    {dmsClient ? (
                      <DocumentDetail
                        client={dmsClient}
                        documentId={preSignoff.document_id}
                        onChanged={() => { void load(tab) }}
                      />
                    ) : (
                      <DmsNotice text={
                        `Open the "${preSignoff.repo_title || preSignoff.repo}" `
                        + `repository to follow this `
                        + `document — this screen has no transport bound to it.`} />
                    )}
                  </div>
                ) : dmsKind ? (
                  <div style={{ padding: '14px 18px 0' }}>
                    {dmsClient ? (
                      <>
                        {!dmsClient.decideApproval && (
                          <DmsNotice text={
                            'You can read this document approval here, but it is '
                            + 'decided in its repository.'} />
                        )}
                        <DmsApprovalReview
                          client={dmsClient}
                          instanceId={selected.id}
                          detail={dmsDetail}
                          embedViewer
                          onError={(m) => notify.error(m)}
                        />
                      </>
                    ) : (
                      <DmsNotice text={
                        `This approval belongs to the document repository `
                        + `"${dmsRepoOf(selected)}". Open that repository to `
                        + `review and decide it — a document release is `
                        + `published by moving its version pointer, which this `
                        + `screen cannot do here.`} />
                    )}
                  </div>
                ) : detailBusy && !model && !modelError ? (
                  <div style={{ padding: 40 }}>
                    <Spinner centered label="Loading record…" />
                  </div>
                ) : modelError ? (
                  <div style={{ padding: 24, color: 'var(--color-error, #ef4444)', fontSize: '0.9125rem' }}>
                    Could not load the transaction form for this record: {modelError}
                  </div>
                ) : sm ? (
                  <div style={{ padding: '12px 0' }}>
                    {validationBanner && (
                      <div
                        role="alert"
                        style={{
                          display: 'flex', gap: 8, alignItems: 'flex-start',
                          margin: '0 0 12px', padding: '10px 12px',
                          border: '1px solid var(--color-error, #ef4444)',
                          borderRadius: 6, fontSize: '0.8825rem',
                          color: 'var(--color-error, #ef4444)',
                          background: 'color-mix(in srgb, var(--color-error, #ef4444) 8%, transparent)',
                        }}
                      >
                        <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{validationBanner.lead}</div>
                          {validationBanner.items.length > 0 && (
                            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                              {validationBanner.items.map((m, i) => (
                                <li key={i}>{m}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                    {isActionRequest(selected) ? (
                      <div style={{
                        display: 'flex', gap: 8, alignItems: 'flex-start',
                        margin: '0 0 12px', padding: '10px 12px',
                        border: '1px solid var(--color-border)', borderRadius: 6,
                        background: 'rgba(99,102,241,0.08)', fontSize: '0.8825rem',
                      }}>
                        <Zap size={15} style={{ flexShrink: 0, marginTop: 1, color: '#4338ca' }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>
                            Approving runs “{actionRequestLabel(selected)}” on this record.
                          </div>
                          <div style={{ color: 'var(--color-text-secondary)', marginTop: 2 }}>
                            The record below is the row the action was requested on, shown
                            for context — it is not a draft and cannot be edited here.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <ChangesPanel
                        changes={changes}
                        childChanges={childChanges}
                        isAdd={(selected.action || '') === 'add'}
                      />
                    )}
                    {/* Header→detail transactions get a tab strip; a flat
                        transaction shows just the header form. The staged tree
                        is fully navigable (no "save the parent first" gate). */}
                    {topForms.length > 1 && (
                      <FormTabs
                        forms={topForms}
                        active={activeFormNo || headerFormNoStr}
                        onChange={setActiveFormNo}
                        childTabsEnabled={true}
                      />
                    )}
                    {(activeFormNo || headerFormNoStr) === headerFormNoStr ? (
                      <FormRenderer
                        model={sm}
                        values={formValues}
                        errors={fieldErrors}
                        onChange={onFieldChange}
                        readOnlyKey={!editing}
                        project={project}
                        objName={selected.obj_name}
                        formNo={headerFormNoStr || undefined}
                        /* In review mode the whole form is read-only; the editing
                           flag flips it. Disabling all fields keeps approvers from
                           mutating values until they explicitly choose Edit. */
                        dynamicProtect={editing ? editProtect : allColumnsProtected(sm)}
                      />
                    ) : model ? (() => {
                      const entry = childForms.find(f => f.formNo === activeFormNo)
                      if (!entry) return null
                      // ChildFormTab fills its container (height:100%) — give it a
                      // sized flex box so its grid scrolls inside the detail panel.
                      return (
                        <div style={{
                          display: 'flex', flexDirection: 'column',
                          height: 420, minHeight: 280, overflow: 'hidden',
                          border: '1px solid var(--color-border)', borderRadius: 6,
                        }}>
                          <ChildFormTab
                            key={entry.formNo}
                            project={project}
                            objName={selected.obj_name}
                            formNo={entry.formNo}
                            sm={entry.sm}
                            parentSm={sm}
                            parentPk={parentPk}
                            parentPkKey={parentPkKey}
                            parentHeaderValues={formValues}
                            model={model}
                            depth={1}
                            store={makeApprovalStore(entry.formNo)}
                            readOnly={!editing}
                          />
                        </div>
                      )
                    })() : null}
                  </div>
                ) : (
                  <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: '0.9125rem' }}>
                    This object has no form to display.
                  </div>
                )}

                <HistoryTimeline history={selected.history_json} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phase 3: out-of-office delegation panel */}
      {showDelegate && (
        <DelegatePanel
          loading={delegLoading}
          delegations={delegations}
          onClose={() => setShowDelegate(false)}
          onSubmit={submitDelegation}
          onRevoke={revokeDelegation}
        />
      )}

      {/* Phase 3: analytics panel (super-approver) */}
      {showAnalytics && (
        <AnalyticsPanel
          loading={analyticsLoading}
          analytics={analytics}
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {/* "Proceed anyway?" confirm for acknowledgeable severity-'W'
          validation warnings raised by approve / staged-record save. */}
      {warningsModal}
    </div>
  )

  // The shared FormRenderer / ChildFormTab read their transports from
  // context; the inbox mounts OUTSIDE a <TxnScreen>, so it provides both
  // (the screen runtime for item_change / lazy loads / validate, the txn
  // runtime for lookups / uploads / notifications) itself.
  const wrapped = (
    <TxnScreenRuntimeProvider value={runtime}>
      <TxnRuntimeProvider value={runtime.txn}>
        {node}
      </TxnRuntimeProvider>
    </TxnScreenRuntimeProvider>
  )
  // A portal would tear an embedded panel out of the layout it was asked to
  // fill, so it is for the modal form only.
  return embedded ? wrapped : createPortal(wrapped, document.body)
}

// ── Master list ───────────────────────────────────────────────────────────

function ApprovalList({
  items, dmsRows, loading, error, tab, selectedId, compact, onOpen,
  selectable, selectedIds, onToggleSelect, allSelected, onToggleSelectAll,
}: {
  items: ApprovalInstance[]
  /** Resolved document titles, keyed by instance id (empty until loaded). */
  dmsRows: Record<string, DmsPendingApproval>
  loading: boolean
  error: string | null
  tab: Tab
  selectedId: string | null
  compact: boolean
  onOpen: (inst: ApprovalInstance) => void
  selectable: boolean
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  allSelected: boolean
  onToggleSelectAll: () => void
}) {
  if (loading) {
    return <div style={{ padding: 40 }}><Spinner centered label="Loading…" /></div>
  }
  if (error) {
    return (
      <div style={{ padding: 24, color: 'var(--color-error, #ef4444)', fontSize: '0.9125rem' }}>
        {error}
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <div style={{
        padding: 40, textAlign: 'center',
        color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
      }}>
        {tab === 'pending'
          ? 'Nothing is waiting for your approval right now.'
          : 'You have no submissions yet.'}
      </div>
    )
  }

  // "Select all" affordance — only meaningful when there is ≥1 pending row to
  // select (own decisions live on the pending tab).
  const hasSelectable = selectable && items.some((i) => i.status === 'pending')

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {hasSelectable && (
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 16px', borderBottom: '1px solid var(--color-border)',
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          cursor: 'pointer', background: 'var(--color-bg)',
        }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onToggleSelectAll}
            title="Select all pending approvals"
          />
          Select all
        </label>
      )}
      {items.map((inst) => {
        const isSel = inst.id === selectedId
        const canSelect = selectable && inst.status === 'pending'
        const checked = selectedIds.has(inst.id)
        const docKind = dmsKindOf(inst)
        const preSignoff = pendingSignoffOf(inst)
        return (
          <div
            key={inst.id}
            style={{
              display: 'flex', alignItems: 'stretch',
              background: isSel ? 'var(--color-accent-weak, rgba(76,141,255,0.16))' : 'transparent',
              borderBottom: '1px solid var(--color-border)',
            }}
            onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = isSel ? 'var(--color-accent-weak, rgba(76,141,255,0.16))' : 'transparent' }}
          >
            {canSelect && (
              <label
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'flex', alignItems: 'flex-start',
                  padding: '14px 4px 0 14px', cursor: 'pointer',
                }}
                title="Select for bulk action"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleSelect(inst.id)}
                />
              </label>
            )}
            <button
              onClick={() => onOpen(inst)}
              {...tw('approval', inst.id, { obj: inst.obj_name, status: inst.status, kind: inst.action })}
              style={{
                textAlign: 'left', flex: 1, minWidth: 0,
                background: 'transparent', border: 'none',
                padding: canSelect ? '12px 16px 12px 8px' : '12px 16px',
                cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 6,
                color: 'inherit',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: '0.9125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {/* A document row is headed by the DOCUMENT. Every row in a
                      repository otherwise reads identically, which is exactly
                      how a queue of them looked before. */}
                  {docKind
                    ? (dmsRows[inst.id]?.title || dmsRowSubtitle(docKind))
                    : (inst.obj_title || inst.obj_name || 'Record')}
                </span>
                <StatusBadge status={inst.status} />
              </div>

              {/* What is staged: a few key header fields for a record, the
                  release it proposes for a document. */}
              {docKind
                ? <DmsRowSummary inst={inst} kind={docKind} row={dmsRows[inst.id]} />
                : preSignoff
                  ? <PreSignoffRowSummary signoff={preSignoff} />
                  : <Summary header={inst.header_json} />}

              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(inst.submitter_email || dmsRows[inst.id]?.submitted_by) && (
                  <span>by {inst.submitter_email || dmsRows[inst.id]?.submitted_by}</span>
                )}
                {inst.submitted_at && <span>· {formatWhen(inst.submitted_at)}</span>}
                <span style={{ textTransform: 'capitalize' }}>
                  {/* A document's engine action is always 'edit'; saying so
                      would describe the plumbing, not the request. */}
                  · {docKind
                    ? (docKind === 'review' ? 'review sign-off' : 'release')
                    : preSignoff ? 'awaiting sign-off'
                    : isActionRequest(inst) ? actionRequestLabel(inst) : (inst.action || 'add')}
                </span>
              </div>

              {!compact && <StepLine inst={inst} />}
            </button>
          </div>
        )
      })}
    </div>
  )
}

// A compact summary of the staged record: the first few non-empty header
// fields as "label: value" chips. Guards null header_json.
function Summary({ header }: { header: Record<string, unknown> | null }) {
  const entries = useMemo(() => {
    if (!header || typeof header !== 'object') return []
    return Object.entries(header)
      .map(([k, v]) => [k, previewValue(v)] as [string, string])
      .filter(([k, v]) => !k.startsWith('_') && v !== '')
      .slice(0, 4)
  }, [header])

  if (entries.length === 0) {
    return <span style={{ fontSize: '0.8225rem', color: 'var(--color-text-secondary)' }}>No preview available</span>
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', fontSize: '0.8225rem' }}>
      {entries.map(([k, v]) => (
        <span key={k} style={{ color: 'var(--color-text-secondary)' }}>
          <span style={{ opacity: 0.7 }}>{prettyLabel(k)}:</span>{' '}
          <span style={{ color: 'var(--color-text)' }}>{truncate(v, 28)}</span>
        </span>
      ))}
    </div>
  )
}

/**
 * One header value as text.
 *
 * `String(value)` on an object yields `[object Object]`, which is worse than
 * showing nothing: it occupies the preview slot that a real field could have
 * had and tells the reader the row is broken. A nested value is summarised by
 * its own most identifying scalar instead, and an empty one drops out.
 */
function previewValue(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) return value.length ? `${value.length} item(s)` : ''
  if (typeof value === 'object') {
    const inner = Object.entries(value as Record<string, unknown>)
      .find(([k, v]) => !k.startsWith('_') && v != null
        && typeof v !== 'object' && String(v).trim() !== '')
    return inner ? `${prettyLabel(inner[0])} ${String(inner[1]).trim()}` : ''
  }
  return String(value).trim()
}

/**
 * The list preview for a DOCUMENT approval.
 *
 * The staged header is a POINTER at a version, not a record, so there are no
 * fields to preview — what the row has to say is which repository it came
 * from, what the decision does, and who it is waiting on.
 */
function DmsRowSummary({ inst, kind, row }: {
  inst: ApprovalInstance
  kind: DmsApprovalKind
  row?: DmsPendingApproval
}) {
  const repo = inst.obj_title || dmsRepoOf(inst)
  const target = row?.target_state || ''
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', fontSize: '0.8225rem',
                  color: 'var(--color-text-secondary)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <FileText size={11} style={{ opacity: 0.75 }} />
        {repo || 'Document'}
      </span>
      <span style={{ color: 'var(--color-text)' }}>
        {kind === 'review'
          ? 'Review sign-off'
          : target ? `Releases as ${target}` : 'Proposed release'}
      </span>
      {row?.pending_roles && <span>· waiting on {row.pending_roles}</span>}
    </div>
  )
}

/**
 * A document submitted for a sign-off nobody has raised yet.
 *
 * There is no staged record to preview — the generic <Summary> read "No
 * preview available", which is true and useless. What the submitter wants to
 * know is where their document sits and who it is with.
 */
function PreSignoffRowSummary({ signoff }: {
  signoff: NonNullable<ReturnType<typeof pendingSignoffOf>>
}) {
  const roles = (signoff.pending_roles || []).filter(Boolean)
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', fontSize: '0.8225rem',
                  color: 'var(--color-text-secondary)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <FileText size={11} style={{ opacity: 0.75 }} />
        {signoff.repo_title || signoff.repo || 'Document'}
      </span>
      <span style={{ color: 'var(--color-text)' }}>
        {signoff.state
          ? `Submitted — in “${signoff.state}”`
          : 'Submitted for sign-off'}
      </span>
      {roles.length > 0 && <span>· waiting on {roles.join(', ')}</span>}
    </div>
  )
}

// The current step: pending activities + the roles/users it's waiting on.
function StepLine({ inst }: { inst: ApprovalInstance }) {
  const acts = (inst.current_activities || []).filter(Boolean)
  const roles = (inst.pending_roles || '').trim()
  if (acts.length === 0 && !roles) return null
  return (
    <div style={{
      fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
      display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
    }}>
      {acts.length > 0 && (
        <span style={{
          background: 'var(--color-bg-secondary)', padding: '1px 7px',
          borderRadius: 10, border: '1px solid var(--color-border)',
        }}>
          Step: {acts.join(', ')}
        </span>
      )}
      {roles && <span>waiting on {roles}</span>}
    </div>
  )
}

// ── Detail panel chrome ────────────────────────────────────────────────────

function DetailHeader({
  inst, tab, editing, busy, sla, dmsKind, dmsTitle, canDecideDms,
  onClose, onApprove, onStartReject, onEdit, onCancelEdit, onSave, onWithdraw,
}: {
  inst: ApprovalInstance
  tab: Tab
  editing: boolean
  busy: boolean
  sla: ApprovalSla | null
  /** Non-empty when this stages a DOCUMENT (see ./dmsBridge). */
  dmsKind: DmsApprovalKind
  /** The document's own title, once resolved. */
  dmsTitle: string
  /** Whether the host can carry the decision out (a bound DMS transport). */
  canDecideDms: boolean
  onClose: () => void
  onApprove: () => void
  onStartReject: () => void
  onEdit: () => void
  onCancelEdit: () => void
  onSave: () => void
  onWithdraw: () => void
}) {
  // Decisions only make sense while pending. The "pending" tab shows
  // approve/reject/edit; the "submissions" tab shows withdraw (own records).
  const isPending = inst.status === 'pending'
  // An action request has no record draft to edit — approve runs the button.
  const isAction = isActionRequest(inst)
  // Neither does a DOCUMENT: an approver reads the real file and signs it off,
  // they never retype a staged copy of it. And when the host cannot reach the
  // repository, offering Approve would offer something that cannot happen.
  const isDms = dmsKind !== ''
  const canDecide = !isDms || canDecideDms
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-bg)', flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <strong style={{ fontSize: '0.9425rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {/* For a document the REPOSITORY is the context, not the subject —
              "Controlled SOP Repository" told an approver nothing about what
              they were being asked to sign off. */}
          {isDms
            ? (dmsTitle || dmsRowSubtitle(dmsKind))
            : (inst.obj_title || inst.obj_name || 'Record')}
        </strong>
        <StatusBadge status={inst.status} />
        {isDms && <DocChip kind={dmsKind} repo={inst.obj_title || dmsRepoOf(inst)} />}
        {isAction && <ActionChip label={actionRequestLabel(inst)} />}
        {sla?.has_sla && <SlaChip sla={sla} />}
        {editing && (
          <span style={{ fontSize: '0.8125rem', color: '#eab308', fontWeight: 600 }}>● editing</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tab === 'pending' && isPending && !editing && canDecide && (
          <>
            {!isAction && !isDms && (
              <button className="btn btn-ghost btn-sm" onClick={onEdit} disabled={busy} title="Edit the staged record">
                <Pencil size={14} /> Edit
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={onStartReject} disabled={busy}
              {...tw('action', 'approval-reject')}
              style={{ color: 'var(--color-error, #ef4444)' }}
              title={isAction ? 'Reject — the action will not run (a comment is required)'
                : isDms ? 'Reject — the document goes back to its owner (a comment is required)'
                : 'Reject (a comment is required)'}>
              <XCircle size={14} /> Reject
            </button>
            <button className="btn btn-primary btn-sm" onClick={onApprove} disabled={busy}
              {...tw('action', 'approval-approve')}
              title={isAction ? `Approve — this runs "${actionRequestLabel(inst)}"`
                : isDms ? 'Approve — clears your level; the document publishes once every level has'
                : 'Approve'}>
              <CheckCircle2 size={14} /> Approve
            </button>
          </>
        )}
        {tab === 'pending' && isPending && editing && (
          <>
            <button className="btn btn-ghost btn-sm" onClick={onCancelEdit} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={busy} title="Save changes to the staged record">
              <Save size={14} /> Save changes
            </button>
          </>
        )}
        {tab === 'submissions' && isPending && (
          <button className="btn btn-ghost btn-sm" onClick={onWithdraw} disabled={busy}
            style={{ color: 'var(--color-error, #ef4444)' }} title="Withdraw this submission">
            <Undo2 size={14} /> Withdraw
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy} title="Back to list">
          <X size={14} />
        </button>
      </div>
    </div>
  )
}

function RejectBox({
  comment, busy, onChange, onCancel, onConfirm,
}: {
  comment: string
  busy: boolean
  onChange: (v: string) => void
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div style={{
      padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
      background: 'rgba(239,68,68,0.06)',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <label style={{ fontSize: '0.8425rem', fontWeight: 600, color: 'var(--color-error, #ef4444)' }}>
        Reason for rejection (required)
      </label>
      <textarea
        value={comment}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder="Explain why this record is being rejected…"
        style={{
          width: '100%', resize: 'vertical', padding: '8px 10px',
          border: '1px solid var(--color-border)', borderRadius: 6,
          background: 'var(--color-bg)', color: 'var(--color-text)',
          fontSize: '0.8825rem', fontFamily: 'inherit',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          className="btn btn-danger btn-sm"
          onClick={onConfirm}
          disabled={busy || !comment.trim()}
          title={!comment.trim() ? 'A comment is required' : 'Confirm rejection'}
        >
          <XCircle size={14} /> Confirm reject
        </button>
      </div>
    </div>
  )
}

// ── History timeline ───────────────────────────────────────────────────────

// "What changed" panel — for an EDIT submission it lists each changed header
// field as old → new (the engine's field-level diff) plus the per-child-form
// row changes (added / removed / modified); for an ADD it notes a new record.
function ChangesPanel({
  changes, childChanges, isAdd,
}: {
  changes: ApprovalChange[]
  childChanges: ApprovalChildChange[]
  isAdd: boolean
}) {
  const [collapsed, setCollapsed] = useState(false)
  if (isAdd) {
    return (
      <div style={{
        margin: '0 0 12px', padding: '8px 12px', borderRadius: 6,
        background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.35)',
        fontSize: '0.8625rem', color: 'var(--color-text)',
      }}>
        <strong>New record</strong> — this submission creates a new record; all values are new.
      </div>
    )
  }
  const totalChild = childChanges.reduce((n, f) => n + f.rows.length, 0)
  const total = changes.length + totalChild
  if (!total) return null
  return (
    <div style={{
      margin: '0 0 14px', padding: '8px 12px', borderRadius: 6,
      background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.35)',
    }}>
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        title={collapsed ? 'Show changes' : 'Hide changes'}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: '0.8625rem', fontWeight: 600, color: 'var(--color-text)',
          marginBottom: collapsed ? 0 : 8, textAlign: 'left',
        }}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <span>Changes in this submission ({total})</span>
      </button>
      {collapsed ? null : <>
      {changes.map((c) => (
        <div key={c.field} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: '0.8425rem' }}>
          <div style={{ minWidth: 110, fontWeight: 600, color: 'var(--color-text)' }}>{c.label}</div>
          <div style={{ flex: 1, color: 'var(--color-text-secondary)', textDecoration: 'line-through', wordBreak: 'break-word' }}>{c.old || '—'}</div>
          <div style={{ color: 'var(--color-text-secondary)' }}>→</div>
          <div style={{ flex: 1, color: '#15803d', fontWeight: 600, wordBreak: 'break-word' }}>{c.new || '—'}</div>
        </div>
      ))}
      {childChanges.map((form) => (
        <div key={form.form_no} style={{ marginTop: changes.length ? 10 : 0 }}>
          <div style={{
            fontSize: '0.8225rem', fontWeight: 600, color: 'var(--color-text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.03em', margin: '4px 0',
          }}>
            {form.form_title}
          </div>
          {form.rows.map((row, i) => (
            <div key={`${row.kind}-${row.label}-${i}`} style={{ padding: '3px 0 3px 6px', fontSize: '0.8425rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ChildKindBadge kind={row.kind} />
                <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{row.label || '(row)'}</span>
              </div>
              {row.fields.map((f, j) => (
                <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0 2px 18px' }}>
                  <div style={{ minWidth: 96, color: 'var(--color-text-secondary)' }}>{f.label}</div>
                  <div style={{ flex: 1, color: 'var(--color-text-secondary)', textDecoration: row.kind === 'modified' ? 'line-through' : undefined, wordBreak: 'break-word' }}>{f.old || '—'}</div>
                  {row.kind === 'modified' && <div style={{ color: 'var(--color-text-secondary)' }}>→</div>}
                  {row.kind !== 'removed' && <div style={{ flex: 1, color: '#15803d', fontWeight: 600, wordBreak: 'break-word' }}>{f.new || '—'}</div>}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
      </>}
    </div>
  )
}

function ChildKindBadge({ kind }: { kind: 'added' | 'removed' | 'modified' }) {
  const palette: Record<string, { bg: string; fg: string; label: string }> = {
    added: { bg: 'rgba(34,197,94,0.16)', fg: '#15803d', label: 'Added' },
    removed: { bg: 'rgba(239,68,68,0.14)', fg: '#b91c1c', label: 'Removed' },
    modified: { bg: 'rgba(234,179,8,0.18)', fg: '#a16207', label: 'Changed' },
  }
  const p = palette[kind] || palette.modified
  return (
    <span style={{
      fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.04em', padding: '1px 6px', borderRadius: 4,
      background: p.bg, color: p.fg,
    }}>{p.label}</span>
  )
}

function HistoryTimeline({ history }: { history: Array<Record<string, unknown>> | null }) {
  const events = Array.isArray(history) ? history : []
  if (events.length === 0) return null
  return (
    <div style={{ padding: '4px 18px 0' }}>
      <div style={{
        fontSize: '0.8425rem', fontWeight: 600, color: 'var(--color-text-secondary)',
        margin: '8px 0', textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        History
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {events.map((ev, i) => {
          const event = String(ev.event ?? ev.action ?? ev.type ?? 'event')
          // Prefer a name over a key: `by` is the raw user id, which told a
          // reader nothing except that somebody did it. The engine stamps
          // `by_email` alongside it, so the id is the LAST resort, not the first.
          const by = (ev.by_email ?? ev.email ?? ev.user ?? ev.actor ?? ev.by) as string | undefined
          const at = (ev.at ?? ev.timestamp ?? ev.date) as string | undefined
          const note = (ev.comment ?? ev.reason ?? ev.note) as string | undefined
          return (
            <li key={i} style={{ display: 'flex', gap: 10, paddingBottom: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%', marginTop: 3,
                  background: eventColor(event), flexShrink: 0,
                }} />
                {i < events.length - 1 && (
                  <span style={{ flex: 1, width: 2, background: 'var(--color-border)', marginTop: 2 }} />
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.8625rem' }}>
                <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{event}</span>
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  {by ? by : 'system'}{at ? ` · ${formatWhen(at)}` : ''}
                </span>
                {note && (
                  <span style={{ fontSize: '0.8225rem', color: 'var(--color-text)', opacity: 0.9 }}>
                    “{note}”
                  </span>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

// ── Small shared bits ──────────────────────────────────────────────────────

function TabPill({
  active, icon, label, onClick,
}: {
  active: boolean
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
        fontSize: '0.8425rem', fontWeight: 600,
        background: active ? 'var(--color-bg)' : 'transparent',
        color: active ? 'var(--color-text)' : 'var(--color-text-secondary)',
        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
      }}
    >
      {icon}{label}
    </button>
  )
}

function StatusBadge({ status }: { status: ApprovalInstance['status'] }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    pending: { bg: 'rgba(234,179,8,0.15)', fg: '#b45309', label: 'Pending' },
    approved: { bg: 'rgba(34,197,94,0.15)', fg: '#15803d', label: 'Approved' },
    rejected: { bg: 'rgba(239,68,68,0.15)', fg: '#b91c1c', label: 'Rejected' },
    cancelled: { bg: 'rgba(100,116,139,0.15)', fg: '#475569', label: 'Cancelled' },
    // Submitted, but no sign-off raised yet — see `pendingSignoffOf`. Not a
    // status the approval store ever writes.
    in_review: { bg: 'rgba(59,130,246,0.15)', fg: '#1d4ed8', label: 'In review' },
  }
  const s = map[status] || map.pending
  return (
    <span style={{
      fontSize: '0.8125rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
      background: s.bg, color: s.fg, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  )
}

// Marks a row as a DOCUMENT rather than a record, and names the repository it
// came from — which, once the document's own title takes the headline, is the
// only place the repository is still stated.
function DocChip({ kind, repo }: { kind: DmsApprovalKind; repo: string }) {
  return (
    <span
      title={kind === 'review'
        ? `Review comments in "${repo}" — approving marks them accepted`
        : `A document release in "${repo}" — approving publishes this version`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.8125rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
        background: 'rgba(20,184,166,0.15)', color: '#0f766e', whiteSpace: 'nowrap',
        maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >
      <FileText size={11} /> {repo || 'Document'}
    </span>
  )
}

// A plain statement in the detail pane when the inbox can show a document
// approval but not act on it. Deliberately not styled as an error: nothing has
// failed, the decision simply lives somewhere else.
function DmsNotice({ text }: { text: string }) {
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start',
      margin: '0 0 12px', padding: '10px 12px',
      border: '1px solid var(--color-border)', borderRadius: 6,
      background: 'rgba(20,184,166,0.08)', fontSize: '0.8825rem',
    }}>
      <FileText size={15} style={{ flexShrink: 0, marginTop: 1, color: '#0f766e' }} />
      <div style={{ minWidth: 0 }}>{text}</div>
    </div>
  )
}

// Names the BUTTON an action request will run when approved, so an approver
// never has to guess what "Approve" does on a record that looks unchanged.
function ActionChip({ label }: { label: string }) {
  return (
    <span
      title={`Approving runs "${label}" on this record`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.8125rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
        background: 'rgba(99,102,241,0.15)', color: '#4338ca', whiteSpace: 'nowrap',
      }}
    >
      <Zap size={11} /> {label}
    </span>
  )
}

// SLA position of the open instance's current step: an overdue chip (red, with
// hours over) or a subtle "due <relative>" chip. Only rendered when has_sla.
function SlaChip({ sla }: { sla: ApprovalSla }) {
  if (sla.overdue) {
    const over = typeof sla.hours_over === 'number' ? formatHours(sla.hours_over) : null
    return (
      <span
        title={sla.due_at ? `Was due ${formatWhen(sla.due_at)}` : 'Overdue'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: '0.8125rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
          background: 'rgba(239,68,68,0.15)', color: '#b91c1c', whiteSpace: 'nowrap',
        }}
      >
        <AlarmClock size={11} /> {over ? `Overdue by ${over}` : 'Overdue'}
      </span>
    )
  }
  if (!sla.due_at) return null
  return (
    <span
      title={`Due ${formatWhen(sla.due_at)}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.8125rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
        background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
        border: '1px solid var(--color-border)', whiteSpace: 'nowrap',
      }}
    >
      <AlarmClock size={11} /> Due {formatRelative(sla.due_at)}
    </span>
  )
}

// Build a dynamicProtect map that marks every column protected (1), so the
// shared FormRenderer renders the staged record strictly read-only in review
// mode. Keyed by the SAME identifier FormRenderer reads protect overrides by
// (columnFormKey — see FormRenderer's dynProtect lookup).
function allColumnsProtected(sm: SqlModel): Record<string, 0 | 1> {
  const out: Record<string, 0 | 1> = {}
  for (const col of visibleColumns(sm)) {
    const key = columnFormKey(col as ColumnDef)
    if (key) out[key] = 1
  }
  return out
}

function eventColor(event: string): string {
  const e = event.toLowerCase()
  if (e.includes('approve')) return '#22c55e'
  if (e.includes('reject')) return '#ef4444'
  if (e.includes('withdraw') || e.includes('cancel')) return '#64748b'
  if (e.includes('edit')) return '#eab308'
  return 'var(--color-accent, #6366f1)'
}

function prettyLabel(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

function formatWhen(raw: string): string {
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return raw
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return raw
  }
}

// "in 3h" / "2d ago" — a coarse relative time for the SLA "due" chip.
function formatRelative(raw: string): string {
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return raw
    const diffMs = d.getTime() - Date.now()
    const past = diffMs < 0
    const mins = Math.round(Math.abs(diffMs) / 60000)
    let label: string
    if (mins < 60) label = `${mins}m`
    else if (mins < 60 * 24) label = `${Math.round(mins / 60)}h`
    else label = `${Math.round(mins / (60 * 24))}d`
    return past ? `${label} ago` : `in ${label}`
  } catch {
    return raw
  }
}

// A whole or fractional hours value → "5h" / "1.5h" / "30m".
function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)}m`
  const rounded = Math.round(h * 10) / 10
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}h`
}

// ── Phase 3 panels ──────────────────────────────────────────────────────────

// Out-of-office delegation: grant another user the caller's approval authority
// (optionally time-boxed) + manage existing grants.
function DelegatePanel({
  loading, delegations, onClose, onSubmit, onRevoke,
}: {
  loading: boolean
  delegations: ApprovalDelegation[]
  onClose: () => void
  onSubmit: (toUser: string, opts: { startsAt?: string; endsAt?: string; reason?: string }) => void
  onRevoke: (id: number) => void
}) {
  const [toUser, setToUser] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [reason, setReason] = useState('')

  const submit = () => {
    onSubmit(toUser, {
      ...(startsAt ? { startsAt } : {}),
      ...(endsAt ? { endsAt } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    })
    setToUser(''); setStartsAt(''); setEndsAt(''); setReason('')
  }

  return (
    <PanelShell title="Out of office" icon={<UserCog size={15} />} onClose={onClose} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 18px' }}>
        <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
          While you&rsquo;re away, delegate approvals waiting on you to another user.
        </p>
        <FieldRow label="Delegate to (user id)">
          <input
            value={toUser}
            onChange={(e) => setToUser(e.target.value)}
            placeholder="e.g. alice"
            style={inputStyle}
          />
        </FieldRow>
        <div style={{ display: 'flex', gap: 10 }}>
          <FieldRow label="Starts (optional)" style={{ flex: 1 }}>
            <input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} style={inputStyle} />
          </FieldRow>
          <FieldRow label="Ends (optional)" style={{ flex: 1 }}>
            <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} style={inputStyle} />
          </FieldRow>
        </div>
        <FieldRow label="Reason (optional)">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. annual leave"
            style={inputStyle}
          />
        </FieldRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={!toUser.trim()}>
            <UserCog size={14} /> Delegate
          </button>
        </div>

        <div style={{
          marginTop: 6, borderTop: '1px solid var(--color-border)', paddingTop: 12,
        }}>
          <div style={{
            fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: 8,
          }}>
            Your delegations
          </div>
          {loading ? (
            <div style={{ padding: 12 }}><Spinner centered label="Loading…" /></div>
          ) : delegations.length === 0 ? (
            <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
              No active delegations.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {delegations.map((d) => (
                <div key={d.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', border: '1px solid var(--color-border)',
                  borderRadius: 6, fontSize: '0.8625rem',
                  background: d.active ? 'transparent' : 'var(--color-bg-secondary)',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div>
                      <span style={{ opacity: 0.7 }}>to</span>{' '}
                      <strong>{d.to_user}</strong>
                      {!d.active && (
                        <span style={{ marginLeft: 8, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          (inactive)
                        </span>
                      )}
                    </div>
                    {(d.starts_at || d.ends_at || d.reason) && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {d.starts_at && <span>from {formatWhen(d.starts_at)} </span>}
                        {d.ends_at && <span>· until {formatWhen(d.ends_at)} </span>}
                        {d.reason && <span>· {d.reason}</span>}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => onRevoke(d.id)}
                    style={{ color: 'var(--color-error, #ef4444)' }}
                    title="Revoke this delegation"
                  >
                    <Trash2 size={13} /> Revoke
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PanelShell>
  )
}

// Project-wide approval analytics (super-approver only): read-only cards.
function AnalyticsPanel({
  loading, analytics, onClose,
}: {
  loading: boolean
  analytics: ApprovalAnalytics | null
  onClose: () => void
}) {
  return (
    <PanelShell title="Approval analytics" icon={<BarChart3 size={15} />} onClose={onClose} width={600}>
      {loading ? (
        <div style={{ padding: 40 }}><Spinner centered label="Loading analytics…" /></div>
      ) : !analytics ? (
        <div style={{ padding: 24, fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
          No analytics available.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '14px 18px' }}>
          {/* Top stat cards */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <StatCard label="Total" value={String(analytics.total)} />
            <StatCard label="Avg cycle" value={fmtCycle(analytics.avg_cycle_hours)} />
            <StatCard label="Median cycle" value={fmtCycle(analytics.median_cycle_hours)} />
            <StatCard
              label="Rejection rate"
              value={analytics.rejection_rate == null ? '—' : `${Math.round(analytics.rejection_rate * 100)}%`}
            />
          </div>

          {/* Status counts */}
          <Section title="By status">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(['pending', 'approved', 'rejected', 'cancelled'] as const).map((k) => (
                <MiniStat key={k} label={prettyLabel(k)} value={analytics.status_counts?.[k] ?? 0} />
              ))}
            </div>
          </Section>

          {/* Pending aging */}
          <Section title="Pending aging">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <MiniStat label="< 1 day" value={analytics.pending_aging.lt1d} />
              <MiniStat label="1–3 days" value={analytics.pending_aging.d1_3} />
              <MiniStat label="3–7 days" value={analytics.pending_aging.d3_7} />
              <MiniStat label="> 7 days" value={analytics.pending_aging.gt7d} highlight={analytics.pending_aging.gt7d > 0} />
            </div>
          </Section>

          {/* Top approvers */}
          {analytics.top_approvers.length > 0 && (
            <Section title="Top approvers">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {analytics.top_approvers.slice(0, 5).map((a) => (
                  <div key={a.user} style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: '0.8625rem', padding: '3px 0',
                  }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.user}</span>
                    <strong>{a.count}</strong>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {analytics.sampled > 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              Cycle-time metrics sampled over {analytics.sampled} decided record(s).
            </div>
          )}
        </div>
      )}
    </PanelShell>
  )
}

// ── Phase 3 panel building blocks ───────────────────────────────────────────

// A centered modal overlay layered above the inbox. Stops click propagation so
// the inbox's own backdrop-close doesn't fire.
function PanelShell({
  title, icon, width, onClose, children,
}: {
  title: string
  icon: React.ReactNode
  width: number
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9440,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)', color: 'var(--color-text)',
          border: '1px solid var(--color-border)', borderRadius: 10,
          width: `min(${width}px, 94vw)`, maxHeight: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '11px 16px', borderBottom: '1px solid var(--color-border)',
        }}>
          <strong style={{ fontSize: '0.9625rem', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            {icon} {title}
          </strong>
          <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>
        <div style={{ overflow: 'auto' }}>{children}</div>
      </div>
    </div>
  )
}

function FieldRow({
  label, style, children,
}: {
  label: string
  style?: React.CSSProperties
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
        {label}
      </span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px',
  border: '1px solid var(--color-border-strong)', borderRadius: 6,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
  fontSize: '0.8825rem', fontFamily: 'inherit',
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      flex: '1 1 120px', minWidth: 120,
      border: '1px solid var(--color-border)', borderRadius: 8,
      padding: '10px 12px', background: 'var(--color-bg-secondary)',
    }}>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.1125rem', fontWeight: 700 }}>{value}</div>
    </div>
  )
}

function MiniStat({
  label, value, highlight,
}: {
  label: string
  value: number
  highlight?: boolean
}) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px', borderRadius: 16,
      border: '1px solid var(--color-border)',
      background: highlight ? 'rgba(239,68,68,0.12)' : 'var(--color-bg-secondary)',
      fontSize: '0.8425rem',
    }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <strong style={{ color: highlight ? '#b91c1c' : 'var(--color-text)' }}>{value}</strong>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: 8,
      }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function fmtCycle(h: number | null): string {
  if (h == null) return '—'
  return formatHours(h)
}
